import { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { 
  addDays,
  addMonths, 
  endOfMonth, 
  endOfWeek, 
  format, 
  isSameMonth, 
  startOfMonth, 
  startOfWeek 
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { CustomTabs } from '@/components/ui/custom-tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { 
  Search, 
  Users, 
  Plus, 
  ArrowRight, 
  CalendarDays, 
  ChevronLeft, 
  ChevronRight, 
  Clock,
  Power,
  PowerOff,
  Eye,
  EyeOff,
  Edit
} from 'lucide-react'
import { mockMinisterios, mockMinisterioEscalas, getEscalasByMinisterio, mockEscalas } from '@/mocks'
import { cn } from '@/lib/utils'
import { type Ministerio, type MinisterioEscalaItem, type MinisterioEscalaParticipanteStatus, type MinisterioEscalaParticipante } from '@/types'
import { MinisterioFormCard } from '@/components/ministerio-form-card'
import { MinisterioEditModal } from '@/components/ministerio-edit-modal'

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
}

const WEEK_DAYS_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

const MINISTERIO_COLOR_PALETTE = [
  'bg-secondary',
  'bg-emerald-500',
  'bg-sky-500',
  'bg-amber-500',
  'bg-violet-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
]

const STATUS_BADGE_STYLES: Record<MinisterioEscalaParticipanteStatus, string> = {
  confirmado:
    'bg-emerald-500/15 text-emerald-600 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/20 dark:text-emerald-300',
  pendente:
    'bg-amber-500/15 text-amber-600 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/20 dark:text-amber-200',
  ausente:
    'bg-rose-500/15 text-rose-600 ring-1 ring-inset ring-rose-500/30 dark:bg-rose-500/20 dark:text-rose-200',
}

type CalendarAssignment = {
  ministerio: Ministerio
  escala: MinisterioEscalaItem
  date: Date
}

const DEFAULT_CALENDAR_MONTH = (() => {
  const todasEscalas = Object.values(mockMinisterioEscalas).flat()

  if (todasEscalas.length === 0) {
    return new Date()
  }

  const primeiraData = todasEscalas
    .map((escala) => new Date(escala.data))
    .filter((data) => !Number.isNaN(data.getTime()))
    .sort((a, b) => a.getTime() - b.getTime())[0]

  return primeiraData ?? new Date()
})()

export function MinisteriosPage() {
  const navigate = useNavigate()
  const [ministerios, setMinisterios] = useState<Ministerio[]>(() =>
    [...mockMinisterios].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [currentMonth, setCurrentMonth] = useState(DEFAULT_CALENDAR_MONTH)
  const [tabValue, setTabValue] = useState('lista')
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState<CalendarAssignment | null>(null)
  const [isMinisterioFormOpen, setIsMinisterioFormOpen] = useState(false)
  const [showInactiveMinisterios, setShowInactiveMinisterios] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [ministerioToEdit, setMinisterioToEdit] = useState<Ministerio | null>(null)

  // Função para salvar novo ministério
  const handleSaveMinisterio = useCallback(async (novoMinisterio: Omit<Ministerio, 'id' | 'created_at' | 'updated_at'>) => {
    const now = new Date().toISOString()
    // Gerar ID único baseado no timestamp para evitar conflitos
    const id = `ministerio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    const ministerioCompleto: Ministerio = {
      ...novoMinisterio,
      id,
      // Garantir que todos os campos obrigatórios estejam presentes
      nome: novoMinisterio.nome || '',
      descricao: novoMinisterio.descricao || '',
      grupoFuncoes: novoMinisterio.grupoFuncoes || 'outros',
      lider_perfil_id: novoMinisterio.lider_perfil_id || null,
      igreja_id: novoMinisterio.igreja_id || '123e4567-e89b-12d3-a456-426614174002', // ID padrão da igreja
      ativo: novoMinisterio.ativo !== undefined ? novoMinisterio.ativo : true,
      created_at: now,
      updated_at: now
    }

    // Adicionar o novo ministério aos mocks para que seja encontrado pela página de detalhes
    mockMinisterios.push(ministerioCompleto)
    
    setMinisterios(prev => [...prev, ministerioCompleto].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')))
    setIsMinisterioFormOpen(false) // Fechar o card após salvar
  }, [])

  // Função para cancelar o formulário
  const handleCancelForm = useCallback(() => {
    setIsMinisterioFormOpen(false)
  }, [])

  // Função para editar ministério
  const handleEditMinisterio = useCallback((ministerio: Ministerio) => {
    setMinisterioToEdit(ministerio)
    setIsEditModalOpen(true)
  }, [])

  // Função para salvar edição do ministério
  const handleSaveEditMinisterio = useCallback((ministerioAtualizado: Ministerio) => {
    setMinisterios(prev => 
      prev.map(ministerio => {
        if (ministerio.id === ministerioAtualizado.id) {
          // Atualizar também nos mocks
          const mockIndex = mockMinisterios.findIndex(m => m.id === ministerioAtualizado.id)
          if (mockIndex !== -1) {
            mockMinisterios[mockIndex] = ministerioAtualizado
          }
          
          return ministerioAtualizado
        }
        return ministerio
      }).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    )
  }, [])

  // Função para alternar status ativo/inativo do ministério
  const handleToggleMinisterioStatus = useCallback((ministerioId: string) => {
    setMinisterios(prev => 
      prev.map(ministerio => {
        if (ministerio.id === ministerioId) {
          const updatedMinisterio = {
            ...ministerio,
            ativo: !ministerio.ativo,
            updated_at: new Date().toISOString()
          }
          
          // Atualizar também nos mocks
          const mockIndex = mockMinisterios.findIndex(m => m.id === ministerioId)
          if (mockIndex !== -1) {
            mockMinisterios[mockIndex] = updatedMinisterio
          }
          
          return updatedMinisterio
        }
        return ministerio
      }).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    )
  }, [])

  // Filtrar ministérios baseado no termo de busca e status ativo
  const filteredMinisterios = useMemo(() => {
    let filtered = ministerios
    
    // Filtrar por status ativo se não estiver mostrando inativos
    if (!showInactiveMinisterios) {
      filtered = filtered.filter(ministerio => ministerio.ativo)
    }
    
    // Filtrar por termo de busca
    if (searchTerm.trim()) {
      filtered = filtered.filter((ministerio) =>
        ministerio.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (ministerio.descricao && ministerio.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }
    
    return filtered
  }, [ministerios, searchTerm, showInactiveMinisterios])

  // Ordenar ministérios filtrados
  const sortedMinisterios = useMemo(() => {
    return [...filteredMinisterios].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  }, [filteredMinisterios])

  // Mapa de cores dos ministérios
  const ministerioColorMap = useMemo(() => {
    const colorMap: Record<string, string> = {}
    ministerios.forEach((ministerio, index) => {
      colorMap[ministerio.id] = MINISTERIO_COLOR_PALETTE[index % MINISTERIO_COLOR_PALETTE.length]
    })
    return colorMap
  }, [ministerios])

  // Assignments do calendário
  const calendarAssignments = useMemo(() => {
    const assignments: CalendarAssignment[] = []
    
    ministerios.forEach((ministerio) => {
      const escalas = mockMinisterioEscalas[ministerio.id] || []
      escalas.forEach((escala) => {
        const escalaDate = new Date(escala.data)
        if (isSameMonth(escalaDate, currentMonth)) {
          assignments.push({
            ministerio,
            escala,
            date: escalaDate
          })
        }
      })
    })
    
    return assignments
  }, [ministerios, currentMonth])

  // Handler para mudança de busca
  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 })
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

    const days: Date[] = []
    let cursor = calendarStart

    while (cursor <= calendarEnd) {
      days.push(new Date(cursor))
      cursor = addDays(cursor, 1)
    }

    return days
  }, [currentMonth])

  const monthLabel = useMemo(
    () => format(currentMonth, "MMMM 'de' yyyy", { locale: ptBR }),
    [currentMonth]
  )

  const handlePreviousMonth = useCallback(() => {
    setCurrentMonth(prev => startOfMonth(addMonths(prev, -1)))
  }, [])

  const handleNextMonth = useCallback(() => {
    setCurrentMonth(prev => startOfMonth(addMonths(prev, 1)))
  }, [])

  const handleGoToCurrentMonth = useCallback(() => {
    setCurrentMonth(new Date())
  }, [])

  const handleAssignmentClick = useCallback((assignment: CalendarAssignment) => {
    setSelectedAssignment(assignment)
    setIsAssignmentDialogOpen(true)
  }, [])

  const handleAssignmentDialogOpenChange = useCallback((open: boolean) => {
    setIsAssignmentDialogOpen(open)

    if (!open) {
      setSelectedAssignment(null)
    }
  }, [])

  const ministerioTabs = useMemo(
    () => [
      { id: 'lista', label: 'Ministérios', count: sortedMinisterios.length },
      { id: 'escalas', label: 'Escalas', count: Object.values(mockEscalas).flat().length },
      { id: 'calendario', label: 'Calendário', count: calendarAssignments.length },
    ],
    [calendarAssignments.length, sortedMinisterios.length]
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Ministérios</h1>
          <p className="text-muted-foreground">Explore e participe dos ministérios da igreja</p>
        </div>
        <Button 
          size="lg" 
          className="shadow-md shadow-primary/20"
          onClick={() => setIsMinisterioFormOpen(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Ministério
        </Button>
      </motion.div>

      {/* Card de Formulário Inline */}
      {isMinisterioFormOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="mb-6"
        >
          <MinisterioFormCard
            onSave={handleSaveMinisterio}
            onCancel={handleCancelForm}
          />
        </motion.div>
      )}

      {/* Tabs */}
      <CustomTabs
        tabs={ministerioTabs}
        activeTab={tabValue}
        onTabClick={setTabValue}
        className="space-y-6"
      />

      {/* Conteúdo das Tabs */}
      {tabValue === 'lista' && (
        <div className="space-y-6">
          {/* Search e Filtros */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar ministérios..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={showInactiveMinisterios ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowInactiveMinisterios(!showInactiveMinisterios)}
                    className="flex items-center gap-2"
                  >
                    {showInactiveMinisterios ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    {showInactiveMinisterios ? "Ocultar Inativos" : "Mostrar Inativos"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{showInactiveMinisterios ? "Ocultar ministérios inativos" : "Mostrar ministérios inativos"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </motion.div>

          {/* Grid de Ministérios */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {filteredMinisterios.map((ministerio) => (
              <motion.div key={ministerio.id} variants={item} className="h-full">
                <Card 
                  className={cn(
                    "group h-full overflow-hidden transition-all hover:shadow-lg hover:shadow-secondary/10 cursor-pointer flex flex-col",
                    !ministerio.ativo && "opacity-60 border-dashed"
                  )}
                  onClick={() => navigate(`/ministerios/${ministerio.id}`)}
                >
                  <div className={cn(
                    "h-2 w-full bg-gradient-to-r flex-shrink-0",
                    ministerio.ativo 
                      ? "from-secondary to-secondary/60" 
                      : "from-gray-400 to-gray-300"
                  )} />
                  <CardHeader className="pb-3 flex-shrink-0">
                    <div className="mb-3 flex items-start justify-between">
                      <div className={cn(
                        "flex h-12 w-12 items-center justify-center rounded-xl transition-transform group-hover:scale-110",
                        ministerio.ativo 
                          ? "bg-secondary/10" 
                          : "bg-gray-100"
                      )}>
                        <Users className={cn(
                          "h-6 w-6",
                          ministerio.ativo ? "text-secondary" : "text-gray-400"
                        )} />
                      </div>
                      {!ministerio.ativo && (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                          Inativo
                        </Badge>
                      )}
                    </div>
                    <CardTitle className={cn(
                      "group-hover:text-secondary min-h-[1.5rem] line-clamp-2",
                      !ministerio.ativo && "text-gray-500"
                    )}>
                      {ministerio.nome}
                    </CardTitle>
                    <CardDescription className={cn(
                      "line-clamp-3 min-h-[4.5rem]",
                      !ministerio.ativo && "text-gray-400"
                    )}>
                      {ministerio.descricao}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3 flex-grow flex flex-col justify-end">
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/ministerios/${ministerio.id}`)
                        }}
                      >
                        Ver Detalhes
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant={ministerio.ativo ? "destructive" : "default"}
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleToggleMinisterioStatus(ministerio.id)
                              }}
                            >
                              {ministerio.ativo ? (
                                <PowerOff className="h-4 w-4" />
                              ) : (
                                <Power className="h-4 w-4" />
                              )}
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{ministerio.ativo ? "Desativar ministério" : "Ativar ministério"}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEditMinisterio(ministerio)
                              }}
                              disabled={!ministerio.ativo}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Editar ministério</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {filteredMinisterios.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="rounded-lg border bg-card p-12 text-center"
            >
              <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">Nenhum ministério encontrado</h3>
              <p className="text-sm text-muted-foreground">
                Tente ajustar sua pesquisa ou crie um novo ministério
              </p>
            </motion.div>
          )}
        </div>
      )}

      {tabValue === 'escalas' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-secondary" />
                Escalas dos Ministérios
              </CardTitle>
              <CardDescription>
                Visualize todas as escalas criadas para cada ministério
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {mockMinisterios.map((ministerio) => {
                const escalasMinisterio = getEscalasByMinisterio(ministerio.id)
                
                if (escalasMinisterio.length === 0) return null
                
                return (
                  <div key={ministerio.id} className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary/10">
                          <Users className="h-5 w-5 text-secondary" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{ministerio.nome}</h3>
                          <p className="text-sm text-muted-foreground">
                            {escalasMinisterio.length} escala{escalasMinisterio.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/ministerios/${ministerio.id}?tab=escala`)}
                      >
                        Ver Detalhes
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {escalasMinisterio.map((escala) => (
                        <Card key={escala.id} className="h-full border-l-4 border-l-secondary flex flex-col">
                          <CardContent className="p-4 flex flex-col flex-grow">
                            <div className="space-y-2 flex-grow">
                              <div className="flex items-center justify-between flex-shrink-0">
                                <Badge variant="outline" className="text-xs">
                                  {new Date(escala.data).toLocaleDateString('pt-BR')}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {escala.horario}
                                </span>
                              </div>
                              
                              <div className="space-y-1 flex-grow">
                                <p className="text-sm font-medium">Equipe:</p>
                                <div className="flex flex-wrap gap-1">
                                  {escala.equipe.slice(0, 3).map((membro, index) => (
                                    <Badge key={index} variant="secondary" className="text-xs">
                                      {membro.funcao}
                                    </Badge>
                                  ))}
                                  {escala.equipe.length > 3 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{escala.equipe.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              
                              {escala.recorrencia && escala.recorrencia !== 'nenhuma' && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  <span>
                                    {escala.recorrencia === 'primeiro-domingo' && 'Primeiro domingo'}
                                    {escala.recorrencia === 'segundo-domingo' && 'Segundo domingo'}
                                    {escala.recorrencia === 'terceiro-domingo' && 'Terceiro domingo'}
                                    {escala.recorrencia === 'quarto-domingo' && 'Quarto domingo'}
                                    {escala.recorrencia === 'ultimo-domingo' && 'Último domingo'}
                                  </span>
                                </div>
                              )}
                              
                              {escala.observacoes && (
                                <p className="text-xs text-muted-foreground line-clamp-2">
                                  {escala.observacoes}
                                </p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )
              })}
              
              {Object.values(mockEscalas).flat().length === 0 && (
                <div className="text-center py-12">
                  <CalendarDays className="mx-auto h-12 w-12 text-muted-foreground" />
                  <h3 className="mt-2 text-sm font-semibold">Nenhuma escala encontrada</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    As escalas criadas aparecerão aqui.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {tabValue === 'calendario' && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
            <Card className="overflow-hidden border border-border/60 shadow-lg shadow-secondary/5">
              <CardHeader className="space-y-1 border-b bg-muted/40 pb-6">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <CalendarDays className="h-5 w-5 text-secondary" />
                  Rota mensal de ministérios
                </CardTitle>
                <CardDescription>
                  Visualize as escalas confirmadas de cada ministério ao longo do mês e acesse os
                  detalhes com um clique.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">
                      Escalas do mês
                    </p>
                    <p className="text-2xl font-semibold capitalize text-foreground">
                      {monthLabel}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-full border border-border/60 bg-background/80 shadow-sm transition hover:border-secondary hover:text-secondary"
                      onClick={handlePreviousMonth}
                      aria-label="Mês anterior"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-full border border-border/60 bg-background/80 shadow-sm transition hover:border-secondary hover:text-secondary"
                      onClick={handleNextMonth}
                      aria-label="Próximo mês"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 rounded-full border-border/60 bg-background/80"
                      onClick={handleGoToCurrentMonth}
                    >
                      <Clock className="h-4 w-4" />
                      Mês atual
                    </Button>
                  </div>
                </div>

                <TooltipProvider delayDuration={120}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-7 gap-2 text-center text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
                      {WEEK_DAYS_LABELS.map((weekDay) => (
                        <div
                          key={weekDay}
                          className="rounded-lg bg-muted/40 py-2 text-muted-foreground/80"
                        >
                          {weekDay}
                        </div>
                      ))}
                    </div>
                    <div className="overflow-x-auto">
                      <div className="grid min-w-[640px] grid-cols-7 gap-2 sm:min-w-0">
                        {calendarDays.map((day) => {
                          const dayAssignments = calendarAssignments.filter((assignment) =>
                            format(assignment.date, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
                          )

                          return (
                            <div
                              key={day.toISOString()}
                              className={cn(
                                'relative min-h-[80px] rounded-lg border border-border/40 bg-background/60 p-2 transition-colors hover:bg-muted/40',
                                !isSameMonth(day, currentMonth) && 'opacity-40'
                              )}
                            >
                              <div className="mb-1 text-xs font-medium text-muted-foreground">
                                {format(day, 'd')}
                              </div>
                              <div className="space-y-1">
                                {dayAssignments.slice(0, 3).map((assignment) => (
                                  <Tooltip key={assignment.escala.id}>
                                    <TooltipTrigger asChild>
                                      <button
                                        className={cn(
                                          'block w-full rounded px-1.5 py-0.5 text-left text-[0.65rem] font-medium text-white transition-opacity hover:opacity-80',
                                          ministerioColorMap[assignment.ministerio.id]
                                        )}
                                        onClick={() => handleAssignmentClick(assignment)}
                                      >
                                        {assignment.ministerio.nome}
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="max-w-xs">
                                      <div className="space-y-1">
                                        <p className="font-semibold">{assignment.ministerio.nome}</p>
                                        <p className="text-xs">{assignment.escala.atividade}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {format(assignment.date, "HH:mm", { locale: ptBR })}
                                        </p>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                ))}
                                {dayAssignments.length > 3 && (
                                  <div className="text-[0.6rem] text-muted-foreground">
                                    +{dayAssignments.length - 3} mais
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </TooltipProvider>

                <div className="rounded-lg border border-dashed border-border/60 bg-muted/30 p-4 text-sm text-muted-foreground">
                  <p>
                    Use as setas para navegar entre os meses e clique nas marcações coloridas para
                    conhecer os responsáveis de cada escala.
                  </p>
                  {calendarAssignments.length === 0 && (
                    <p className="mt-2 text-xs text-muted-foreground/70">
                      Nenhuma escala cadastrada para este mês. Explore outros períodos ou cadastre
                      novas escalas na área do ministério.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Dialog open={isAssignmentDialogOpen} onOpenChange={handleAssignmentDialogOpenChange}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-lg">
                    <span
                      className={cn(
                        'h-2.5 w-2.5 rounded-full',
                        selectedAssignment
                          ? ministerioColorMap[selectedAssignment.ministerio.id]
                          : 'bg-secondary'
                      )}
                    />
                    {selectedAssignment?.ministerio.nome ?? 'Detalhes da escala'}
                  </DialogTitle>
                  <DialogDescription>
                    Consulte a escala completa e os responsáveis pelo ministério selecionado.
                  </DialogDescription>
                </DialogHeader>
                {selectedAssignment && (
                  <div className="space-y-4">
                    <div className="space-y-1 text-sm">
                      <p className="font-medium text-foreground">
                        {selectedAssignment.escala.atividade}
                      </p>
                      <p className="text-muted-foreground">
                        {format(selectedAssignment.date, "EEEE, dd 'de' MMMM 'às' HH:mm", {
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h5 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Integrantes escalados
                      </h5>
                      <div className="space-y-2">
                        {selectedAssignment.escala.membros.map((membro: MinisterioEscalaParticipante) => (
                          <div
                            key={`${selectedAssignment.escala.id}-${membro.id}`}
                            className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/40 px-3 py-2"
                          >
                            <div>
                              <p className="text-sm font-semibold leading-tight text-foreground">
                                {membro.nome}
                              </p>
                              <p className="text-[0.7rem] uppercase tracking-wide text-muted-foreground">
                                {membro.funcao}
                              </p>
                            </div>
                            <span
                              className={cn(
                                'rounded-full px-2 py-0.5 text-[0.7rem] font-semibold capitalize',
                                STATUS_BADGE_STYLES[membro.status]
                              )}
                            >
                              {membro.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
        </motion.div>
      )}

      {/* Modal de Edição de Ministério */}
      <MinisterioEditModal
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        ministerio={ministerioToEdit}
        onSave={handleSaveEditMinisterio}
      />
    </div>
  )
}
